import React, { ReactNode, createContext, useCallback, useContext, useState } from 'react';
import {
  PackageQuote,
  PartialFinancialConfiguration,
  PartialFinancialProduct,
  QuoteAddressType,
  QuoteStatus,
} from '@graphql';

interface PackageForm {
  opportunityId: string;
  financialProduct: PartialFinancialProduct | null;
  productConfiguration: PartialFinancialConfiguration | null;
  status: QuoteStatus | null;
  address: QuoteAddressType | null;
  enterprisePartnerId: string;
  enterprisePartnerConfig: {
    title?: string | null;
    cmsContentSlug?: string | null;
  } | null;
}

export interface SelectedCategory {
  name: string;
  slug: string;
  quotes: PackageQuote[];
}

interface PackageContextType {
  packageForm: PackageForm;
  setPackageForm: (data: Partial<PackageForm>) => void;
}

const PackageContext = createContext<PackageContextType | undefined>(undefined);

export const PackageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [packageForm, setPackageForm] = useState<PackageForm>({
    opportunityId: '',
    financialProduct: null,
    productConfiguration: null,
    status: null,
    address: null,
    enterprisePartnerId: '',
    enterprisePartnerConfig: null,
  });

  const updatePackageForm = useCallback((data: Partial<PackageForm>) => {
    setPackageForm(prevData => ({
      ...prevData,
      ...data,
    }));
  }, []);

  return (
    <PackageContext.Provider
      value={{
        packageForm,
        setPackageForm: updatePackageForm,
      }}
    >
      {children}
    </PackageContext.Provider>
  );
};

export const usePackageContext = (): PackageContextType => {
  const context = useContext(PackageContext);
  if (!context) {
    throw new Error('usePackageContext must be used within a PackageProvider');
  }
  return context;
};
